import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Button, Alert, Row, Divider } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import Input from '../elements/input/Input';
import { FloatingLabelInput, FloatingLabelSearchSelect } from '../elements/floatingLabelInput/FloatingLabelInput';
import { fetchProducts, fetchUserVipStatus, previewAdminOrder } from '../../api/webshop-products-orders-api';
import { fetchUsers } from '../../api/user-api';

const PRODUCT_LABEL_MAX = 35;
const truncate = (str, max) =>
  str && str.length > max ? str.slice(0, max) + '…' : str;

const EMPTY_PRODUCT_ROW = { productId: '', quantity: 1 };
const DELIVERY_THRESHOLD_VIP = 12_000;

function quantityDiscountRate(qty) {
  if (qty >= 4) return 0.15;
  if (qty >= 2) return 0.10;
  return 0;
}

// Edit mode only — create mode uses the backend's POST /orders/admin/preview.
// Mirrors the backend: priceOrderProducts (VIP price substitution +
// per-line quantity discount) and computeDeliveryFee (src/utils/pricing.util.ts)
// in src/services/order-pricing.service.ts. `context` carries the inputs the
// preview can't derive from the product list alone: whether the order's
// customer is VIP, and whether the original order was a pickup (frozen from the
// order when editing rather than re-derived).
function computeSummary(productRows, products, context = {}) {
  const { hasVip = false, isPickup = false } = context;

  const resolved = productRows
    .map((row) => {
      const product = products.find((p) => p._id === row.productId);
      if (!product) return null;
      const qty = Math.max(1, Number(row.quantity) || 1);
      const usesVipPrice = hasVip && !!product.vip_price;
      const price = usesVipPrice ? product.vip_price : product.price;
      const lineTotal = price * qty;
      const discountRate = product.hasQuantityDiscount ? quantityDiscountRate(qty) : 0;
      const discountAmount = Math.round(lineTotal * discountRate);
      return {
        name: product.name,
        price,
        usesVipPrice,
        qty,
        lineTotal,
        discountRate,
        discountAmount,
        isFrozen: product.isFrozenProduct === true,
      };
    })
    .filter(Boolean);

  if (resolved.length === 0) return null;

  const hasFrozen = resolved.some((p) => p.isFrozen);
  const subtotal = resolved.reduce((sum, p) => sum + p.lineTotal, 0);
  const totalDiscount = resolved.reduce((sum, p) => sum + p.discountAmount, 0);

  let deliveryFee;
  if (isPickup) deliveryFee = 'free';
  // Frozen is always 990, VIP included.
  else if (hasFrozen) deliveryFee = 990;
  else if (hasVip && subtotal - totalDiscount >= DELIVERY_THRESHOLD_VIP)
    deliveryFee = 'free';
  else deliveryFee = 499;

  const deliveryAmount = deliveryFee === 'free' ? 0 : deliveryFee;
  const total = subtotal - totalDiscount + deliveryAmount;

  return { resolved, subtotal, totalDiscount, deliveryFee, total };
}

function fmt(n) {
  return n.toLocaleString('sr-RS');
}

const userLabel = (u) => {
  const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return truncate(name ? `${name} — ${u.email}` : u.email, 45);
};

// Rows the backend preview can price: a product chosen and a positive whole quantity.
const priceableRows = (rows) =>
  rows
    .filter((row) => row.productId && Number.isInteger(Number(row.quantity)) && Number(row.quantity) >= 1)
    .map((row) => ({ productId: row.productId, quantity: Number(row.quantity) }));

const CreateOrderModal = ({ isOpen, onClose, onConfirm, loading, mode = 'create', order = null }) => {
  const [customerDetails, setCustomerDetails] = useState({ fullName: '', email: '', phone: '' });
  const [shippingAddress, setShippingAddress] = useState({ city: '', postalCode: '', streetAndNumber: '' });
  const [productRows, setProductRows] = useState([{ ...EMPTY_PRODUCT_ROW }]);
  const [pointsLineItems, setPointsLineItems] = useState([]);
  const [hasVip, setHasVip] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productsLoading, setProductsLoading] = useState(false);
  // Create mode: optional existing user the order is placed for (null = guest order).
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  // Create mode: breakdown computed by the backend for the selected user (or guest).
  const [backendPreview, setBackendPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  // Create mode: what the backend resolved for the selected user (profile, then latest order).
  const [userDefaults, setUserDefaults] = useState(null);
  const [userDefaultsLoading, setUserDefaultsLoading] = useState(false);
  // Guards against a slow defaults response for a user who's no longer selected.
  const selectedUserRef = useRef(null);

  // Ids chosen in the product rows, read by loadProducts without re-creating it every render.
  const selectedProductIdsRef = useRef(new Set());
  selectedProductIdsRef.current = new Set(productRows.map((r) => r.productId).filter(Boolean));

  const loadProducts = useCallback(async (search = '') => {
    setProductsLoading(true);
    const { data } = await fetchProducts({ page: 1, limit: 20, search: search || null });
    if (data) {
      // Keep already-chosen products in the list so their selects keep showing a name, not an id.
      setProducts((prev) => {
        const found = new Set(data.products.map((p) => p._id));
        const kept = prev.filter((p) => selectedProductIdsRef.current.has(p._id) && !found.has(p._id));
        return [...kept, ...data.products];
      });
    }
    setProductsLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) loadProducts();
  }, [isOpen, loadProducts]);

  useEffect(() => {
    const timer = setTimeout(() => loadProducts(productSearch), 300);
    return () => clearTimeout(timer);
  }, [productSearch, loadProducts]);

  const loadUsers = useCallback(async (search = '') => {
    setUsersLoading(true);
    const { data } = await fetchUsers({ page: 1, limit: 20, filter: { status: 'ACTIVE' }, search: search || null });
    if (data?.users) {
      // Keep the selected user in the list so the select can still show its label.
      setUsers((prev) => {
        const selected = prev.find((u) => u._id === selectedUserId);
        return selected && !data.users.some((u) => u._id === selected._id) ? [selected, ...data.users] : data.users;
      });
    }
    setUsersLoading(false);
  }, [selectedUserId]);

  useEffect(() => {
    if (!isOpen || mode !== 'create') return;
    const timer = setTimeout(() => loadUsers(userSearch), 300);
    return () => clearTimeout(timer);
  }, [isOpen, mode, userSearch]); // eslint-disable-line

  // Create mode: re-price on the backend whenever the user or the product rows change.
  const previewRowsKey = JSON.stringify(priceableRows(productRows));
  useEffect(() => {
    if (!isOpen || mode !== 'create') return;
    const rows = JSON.parse(previewRowsKey);
    if (rows.length === 0) {
      setBackendPreview(null);
      setPreviewError('');
      setPreviewLoading(false);
      return;
    }
    let cancelled = false;
    // Set immediately, not after the debounce, so Create can't be clicked against stale totals.
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      const { data, error: err } = await previewAdminOrder({
        userId: selectedUserId || undefined,
        products: rows,
        paymentMethod: 'cash_on_delivery',
      });
      if (cancelled) return;
      setPreviewLoading(false);
      setBackendPreview(data);
      setPreviewError(err || '');
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isOpen, mode, selectedUserId, previewRowsKey]);

  // Pre-fill (edit mode) or reset (create mode) whenever the modal opens for a
  // given target order. Keyed on order?._id (not just isOpen) so opening a
  // second edit target right after another closes still re-seeds correctly.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    if (mode === 'edit' && order) {
      const editableProducts = (order.products || []).filter((p) => p.boughtWithPoints !== true);
      const pointsProducts = (order.products || []).filter((p) => p.boughtWithPoints === true);

      setCustomerDetails({
        fullName: order.customerDetails?.fullName || '',
        email: order.customerDetails?.email || '',
        phone: order.customerDetails?.phone || '',
      });
      setShippingAddress({
        city: order.shippingAddress?.city || '',
        postalCode: order.shippingAddress?.postalCode || '',
        streetAndNumber: order.shippingAddress?.streetAndNumber || '',
      });
      setProductRows(
        editableProducts.map((p) => ({
          productId: (p.productId?._id ?? p.productId)?.toString() || '',
          quantity: p.quantity,
        }))
      );
      setPointsLineItems(pointsProducts);
      // Seed the product-search results with the order's own line items so
      // FloatingLabelSearchSelect can resolve their labels without a fresh search.
      setProducts((prev) => {
        const byId = new Map(prev.map((p) => [p._id, p]));
        editableProducts.forEach((p) => {
          const id = (p.productId?._id ?? p.productId)?.toString();
          if (id && !byId.has(id)) byId.set(id, { _id: id, name: p.name, price: p.price });
        });
        return Array.from(byId.values());
      });
      setError('');
      setProductSearch('');

      // The backend applies VIP pricing/thresholds on save if the order's
      // customer is VIP-active — resolve that here so the preview matches.
      // Guest-placed orders (no userId) are never VIP.
      const customerId = order.userId?._id ?? order.userId;
      if (customerId) {
        fetchUserVipStatus(customerId).then(({ data }) => {
          if (!cancelled) setHasVip(!!data?.isActive);
        });
      } else {
        setHasVip(false);
      }
    } else if (mode === 'create') {
      handleReset();
    }

    return () => { cancelled = true; };
  }, [isOpen, mode, order?._id]); // eslint-disable-line

  // Delivery-fee input frozen from the original order on edit (mirrors
  // OrderEditService: isPickup is never re-derived from live state during an
  // edit, only the product totals are).
  const isPickup = mode === 'edit' ? (order?.deliveryFeeMeta?.isPickup ?? order?.isPickup ?? false) : false;

  const summary = useMemo(
    () => computeSummary(productRows, products, { hasVip, isPickup }),
    [productRows, products, hasVip, isPickup]
  );

  const handleReset = () => {
    setCustomerDetails({ fullName: '', email: '', phone: '' });
    setShippingAddress({ city: '', postalCode: '', streetAndNumber: '' });
    setProductRows([{ ...EMPTY_PRODUCT_ROW }]);
    setPointsLineItems([]);
    setHasVip(false);
    setError('');
    setProductSearch('');
    setSelectedUserId(null);
    setUserSearch('');
    setBackendPreview(null);
    setPreviewError('');
    setPreviewLoading(false);
    setUserDefaults(null);
    setUserDefaultsLoading(false);
    selectedUserRef.current = null;
  };

  // Selecting a user prefills the (still editable) customer and address fields with exactly
  // what the backend resolves for them (profile, then their latest order with an address),
  // so the admin sees — and can correct — where the order will go before creating it.
  const handleUserSelect = async (userId) => {
    setError('');
    selectedUserRef.current = userId || null;
    setUserDefaults(null);
    if (!userId) {
      setSelectedUserId(null);
      setUserDefaultsLoading(false);
      setCustomerDetails({ fullName: '', email: '', phone: '' });
      setShippingAddress({ city: '', postalCode: '', streetAndNumber: '' });
      return;
    }

    setSelectedUserId(userId);
    const user = users.find((u) => u._id === userId);
    // Profile values right away; the address arrives with the defaults below.
    setCustomerDetails({
      fullName: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
      email: user?.email || '',
      phone: user?.phone || '',
    });
    setShippingAddress({ city: '', postalCode: '', streetAndNumber: '' });

    setUserDefaultsLoading(true);
    const { data, error: err } = await previewAdminOrder({ userId, products: [] });
    if (selectedUserRef.current !== userId) return;
    setUserDefaultsLoading(false);
    if (err || !data?.userDefaults) {
      setError(err || 'Nije moguće učitati podatke korisnika.');
      return;
    }
    const defaults = data.userDefaults;
    setUserDefaults(defaults);
    setCustomerDetails({
      fullName: defaults.customerDetails.fullName || '',
      email: defaults.customerDetails.email || '',
      phone: defaults.customerDetails.phone || '',
    });
    setShippingAddress(defaults.shippingAddress || { city: '', postalCode: '', streetAndNumber: '' });
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomerDetails((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleProductSelect = (index, productId) => {
    setProductRows((prev) => prev.map((row, i) => (i === index ? { ...row, productId } : row)));
    setError('');
  };

  const handleQuantityChange = (index, e) => {
    const value = e.target.value;
    setProductRows((prev) => prev.map((row, i) => (i === index ? { ...row, quantity: value } : row)));
    setError('');
  };

  const handleAddRow = () => setProductRows((prev) => [...prev, { ...EMPTY_PRODUCT_ROW }]);
  const handleRemoveRow = (index) => setProductRows((prev) => prev.filter((_, i) => i !== index));

  const validate = () => {
    if (!customerDetails.fullName.trim()) { setError('Unesite ime i prezime kupca.'); return false; }
    if (customerDetails.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerDetails.email.trim())) {
      setError('Unesite ispravnu email adresu.');
      return false;
    }
    if (!customerDetails.phone.trim()) { setError('Unesite telefon kupca.'); return false; }
    // Address is always required when creating an order. Existing orders (e.g.
    // pickup orders) may legitimately have no shippingAddress at all, so in edit
    // mode we only require it to be complete if the admin started filling it in.
    const addressTouched = [shippingAddress.city, shippingAddress.postalCode, shippingAddress.streetAndNumber]
      .some((v) => v.trim());
    if (mode === 'create' || addressTouched) {
      if (!shippingAddress.city.trim()) { setError('Unesite grad.'); return false; }
      if (!shippingAddress.postalCode.trim()) { setError('Unesite poštanski broj.'); return false; }
      if (!shippingAddress.streetAndNumber.trim()) { setError('Unesite ulicu i broj.'); return false; }
    }
    return validateProducts();
  };

  const validateProducts = () => {
    for (let i = 0; i < productRows.length; i++) {
      const row = productRows[i];
      if (!row.productId) { setError(`Izaberite proizvod za red ${i + 1}.`); return false; }
      const qty = Number(row.quantity);
      if (!Number.isInteger(qty) || qty < 1) { setError(`Količina za red ${i + 1} mora biti pozitivan ceo broj.`); return false; }
    }
    const ids = productRows.map((r) => r.productId);
    if (new Set(ids).size !== ids.length) { setError('Isti proizvod je dodat više puta. Koristite veću količinu.'); return false; }
    setError('');
    return true;
  };

  const handleOk = async () => {
    if (!validate()) return;

    if (mode === 'edit') {
      // Backend edit DTO fields are each optional-but-non-empty-if-present, so
      // only include fields the admin actually filled in (omitting a group
      // entirely, e.g. shippingAddress on an order with no address, leaves it
      // untouched rather than sending empty strings that would fail validation).
      const onlyFilled = (obj) => {
        const out = {};
        Object.entries(obj).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim()) out[key] = value.trim();
        });
        return out;
      };
      const payload = {};
      const filledCustomerDetails = onlyFilled(customerDetails);
      const filledShippingAddress = onlyFilled(shippingAddress);
      if (Object.keys(filledCustomerDetails).length > 0) payload.customerDetails = filledCustomerDetails;
      if (Object.keys(filledShippingAddress).length > 0) payload.shippingAddress = filledShippingAddress;
      if (productRows.length > 0) {
        payload.products = productRows.map((row) => ({ productId: row.productId, quantity: Number(row.quantity) }));
      }
      // Don't reset on edit — a failed save shouldn't blank a still-open, pre-filled form.
      // The next successful open re-seeds via the pre-fill effect above.
      await onConfirm(payload);
      return;
    }

    const payload = {
      products: productRows.map((row) => ({ productId: row.productId, quantity: Number(row.quantity) })),
      payment: { method: 'cash_on_delivery' },
    };
    const trimmed = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v.trim()]));

    // The form shows exactly what will be used (prefilled from userDefaults for a selected
    // user), so it's always sent as-is — edits are never silently replaced by the backend.
    const details = trimmed(customerDetails);
    const fallbackEmail = selectedUserId ? userDefaults?.customerDetails.email : 'korisnik.nema@email.com';
    payload.customerDetails = { ...details, email: details.email || fallbackEmail || 'korisnik.nema@email.com' };
    payload.shippingAddress = trimmed(shippingAddress);
    if (selectedUserId) payload.userId = selectedUserId;

    // Reset only on success; the parent closes the modal itself.
    const ok = await onConfirm(payload);
    if (ok !== false) handleReset();
  };

  const handleCancel = () => { handleReset(); onClose(); };

  const userOptions = users.map((u) => ({ value: u._id, label: userLabel(u) }));

  // Create mode: why the Create button is disabled, shown under it (null = enabled).
  let createBlockedReason = null;
  if (mode === 'create') {
    if (userDefaultsLoading) createBlockedReason = 'Učitavanje podataka korisnika...';
    else if (previewLoading) createBlockedReason = 'Računanje pregleda...';
    else if (previewError) createBlockedReason = 'Pregled nije dostupan — proverite grešku iznad.';
    else if (backendPreview?.hasError) createBlockedReason = 'Ispravite greške navedene u pregledu.';
  }

  const productOptions = products.map((p) => ({
    value: p._id,
    label: truncate(`${p.name} — ${p.price}`, PRODUCT_LABEL_MAX),
  }));

  return (
    <Modal
      className="create_order_modal"
      title={mode === 'edit' ? 'Izmeni porudžbinu' : 'Kreiraj porudžbinu'}
      open={isOpen}
      onCancel={handleCancel}
      width={1100}
      style={{ top: 20 }}
      footer={null}
    >
      <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>

        <form className="create_user_modal_form" autoComplete="off" style={{ flex: 1, minWidth: 0 }}>
          <Divider orientation="left" style={{ fontSize: 13, color: '#888' }}>Podaci o kupcu</Divider>
          <Row className="create_user_modal_row" style={{ gap: 12, flexDirection: 'column' }}>
            {mode === 'create' && (
              <>
                <FloatingLabelSearchSelect
                  label="Korisnik (opciono)"
                  placeholder="Pretraži po imenu ili email-u..."
                  value={selectedUserId || undefined}
                  onChange={handleUserSelect}
                  onSearch={setUserSearch}
                  loading={usersLoading}
                  options={userOptions}
                  allowClear
                />
                <div style={{ fontSize: 12, color: '#888', marginTop: -6 }}>
                  {selectedUserId
                    ? 'Porudžbina se kreira za izabranog korisnika: VIP cena, popust preporuke, besplatna dostava i poeni lojalnosti važe kao da je sam poručio. Podaci su popunjeni iz profila i adrese iz poslednje porudžbine (ako postoji) — proverite ih.'
                    : 'Bez izabranog korisnika porudžbina se kreira kao gost.'}
                </div>
                {userDefaultsLoading && (
                  <div style={{ fontSize: 12, color: '#888' }}>Učitavanje podataka korisnika...</div>
                )}
                {userDefaults && (!userDefaults.customerDetails.phone || !userDefaults.shippingAddress) && (
                  <Alert
                    type="warning"
                    showIcon
                    message={
                      !userDefaults.customerDetails.phone && !userDefaults.shippingAddress
                        ? 'Korisnik nema sačuvan telefon ni adresu (nema prethodnih porudžbina) — unesite ih ručno.'
                        : !userDefaults.customerDetails.phone
                          ? 'Korisnik nema sačuvan telefon — unesite ga ručno.'
                          : 'Korisnik nema prethodnu porudžbinu sa adresom — unesite adresu ručno.'
                    }
                  />
                )}
              </>
            )}
            <Input
              label="Ime i prezime"
              id="fullName"
              name="fullName"
              placeholder="npr. Marko Marković"
              value={customerDetails.fullName}
              onChange={handleCustomerChange}
            />
            <Input
              label="Email"
              id="email"
              name="email"
              placeholder="npr. marko@email.com"
              value={customerDetails.email}
              onChange={handleCustomerChange}
            />
            <Input
              label="Telefon"
              id="phone"
              name="phone"
              placeholder="npr. 0641234567"
              value={customerDetails.phone}
              onChange={handleCustomerChange}
            />
          </Row>

          <Divider orientation="left" style={{ fontSize: 13, color: '#888' }}>Adresa isporuke</Divider>
          <Row className="create_user_modal_row" style={{ gap: 12, flexDirection: 'column' }}>
            <Input
              label="Grad"
              id="city"
              name="city"
              placeholder="npr. Novi Sad"
              value={shippingAddress.city}
              onChange={handleAddressChange}
            />
            <Input
              label="Poštanski broj"
              id="postalCode"
              name="postalCode"
              placeholder="npr. 21000"
              value={shippingAddress.postalCode}
              onChange={handleAddressChange}
            />
            <Input
              label="Ulica i broj"
              id="streetAndNumber"
              name="streetAndNumber"
              placeholder="npr. Bulevar oslobođenja 12"
              value={shippingAddress.streetAndNumber}
              onChange={handleAddressChange}
            />
          </Row>

          <Divider orientation="left" style={{ fontSize: 13, color: '#888' }}>Proizvodi</Divider>
          {pointsLineItems.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                `Ova porudžbina sadrži ${pointsLineItems.length} proizvod${pointsLineItems.length === 1 ? '' : 'a'} ` +
                'kupljen(a) poenima lojalnosti — ne mogu se menjati ovde.'
              }
            />
          )}
          <Row style={{ flexDirection: 'column', gap: 10 }}>
            {productRows.map((row, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <FloatingLabelSearchSelect
                    label="Proizvod"
                    placeholder="Pretraži..."
                    value={row.productId || undefined}
                    onChange={(val) => handleProductSelect(index, val)}
                    onSearch={setProductSearch}
                    loading={productsLoading}
                    options={productOptions}
                  />
                </div>
                <div style={{ width: 80 }}>
                  <FloatingLabelInput
                    label="Kom."
                    id={`quantity-${index}`}
                    name={`quantity-${index}`}
                    placeholder="1"
                    value={row.quantity}
                    onChange={(e) => handleQuantityChange(index, e)}
                  />
                </div>
                {productRows.length > 1 && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveRow(index)}
                  />
                )}
              </div>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddRow}
              style={{ width: '100%', marginTop: 4 }}
            >
              Dodaj proizvod
            </Button>
          </Row>

          {error && (
            <Alert message={error} type="error" showIcon style={{ marginTop: 16 }} />
          )}
        </form>

        <div style={{
          width: 300,
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          background: '#fafafa',
          border: '1px solid #e8e8e8',
          borderRadius: 8,
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          marginTop: 22,
        }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: '#888', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Pregled narudžbine
          </div>

          {mode === 'create' ? (
            <BackendSummary preview={backendPreview} loading={previewLoading} error={previewError} />
          ) : !summary ? (
            <div style={{ color: '#bbb', fontSize: 13, marginBottom: 16 }}>Dodajte proizvode...</div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                {summary.resolved.map((item, i) => (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span
                        style={{ color: '#333', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={item.name}
                      >
                        {item.name}
                      </span>
                      <span style={{ color: '#333', whiteSpace: 'nowrap', marginLeft: 8 }}>{fmt(item.lineTotal)}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>
                      {item.qty} × {fmt(item.price)}
                      {item.usesVipPrice && (
                        <span style={{
                          marginLeft: 6,
                          color: '#9a6a00',
                          background: '#fff3d6',
                          borderRadius: 4,
                          padding: '1px 5px',
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                        }}>
                          VIP CENA
                        </span>
                      )}
                    </div>
                    {item.discountAmount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#1a7f37', marginTop: 3 }}>
                        <span>Kol. popust ({Math.round(item.discountRate * 100)}%)</span>
                        <span>−{fmt(item.discountAmount)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ borderTop: '1px solid #e8e8e8', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#555' }}>Međuzbir</span>
                  <span>{fmt(summary.subtotal)}</span>
                </div>
                {summary.totalDiscount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#1a7f37' }}>
                    <span>Popust na količinu</span>
                    <span>−{fmt(summary.totalDiscount)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#555' }}>Dostava (pouzećem)</span>
                  {summary.deliveryFee === 'free' ? (
                    <span style={{ color: '#1a7f37', fontWeight: 600 }}>Besplatna</span>
                  ) : (
                    <span>{fmt(summary.deliveryFee)}</span>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 14,
                  fontWeight: 700,
                  borderTop: '1px solid #e8e8e8',
                  paddingTop: 8,
                  marginTop: 2,
                }}>
                  <span>Ukupno</span>
                  <span>{fmt(summary.total)}</span>
                </div>
              </div>
            </>
          )}

          <Button
            type="primary"
            onClick={handleOk}
            className="btn-primary"
            disabled={loading || createBlockedReason !== null}
            style={{ width: '100%', marginTop: 16 }}
          >
            {mode === 'edit'
              ? (loading ? 'Čuvanje...' : 'Sačuvaj izmene')
              : (loading ? 'Kreiranje...' : 'Kreiraj porudžbinu')}
          </Button>
          {createBlockedReason && !loading && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 6, textAlign: 'center' }}>{createBlockedReason}</div>
          )}
        </div>

      </div>
    </Modal>
  );
};

const summaryRow = { display: 'flex', justifyContent: 'space-between', fontSize: 13 };
const discountRow = { ...summaryRow, color: '#1a7f37' };

// Create-mode breakdown, exactly as computed by POST /orders/admin/preview.
const BackendSummary = ({ preview, loading, error }) => {
  if (error) return <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />;
  if (!preview) {
    return (
      <div style={{ color: '#bbb', fontSize: 13, marginBottom: 16 }}>
        {loading ? 'Računanje...' : 'Dodajte proizvode...'}
      </div>
    );
  }

  const quantityDiscount = preview.discountInfo?.amount ?? 0;
  const referralDiscount = preview.referral?.totalAmount ?? 0;
  const upsell = preview.upsellOffer;
  // originalSum − subtotal also contains the upsell discount; only the rest is VIP saving.
  const vipSaving = preview.vipSum != null
    ? preview.originalSum - preview.subtotal - (upsell?.discountAmount ?? 0)
    : 0;

  // Quantity/upsell discounts aren't rounded on the backend. Show every amount with the same
  // precision: 2 decimals if any of them is fractional, whole numbers otherwise.
  const amounts = [
    ...preview.products.flatMap((p) => [p.price, p.price * p.quantity]),
    preview.subtotal, quantityDiscount, referralDiscount, vipSaving, preview.total,
    upsell?.regularPrice ?? 0,
    preview.deliveryFee === 'free' ? 0 : preview.deliveryFee,
  ];
  const decimals = amounts.some((n) => Math.abs(n - Math.round(n)) > 0.001) ? 2 : 0;
  const money = (n) =>
    Number(n).toLocaleString('sr-RS', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  return (
    <div style={{ opacity: loading ? 0.5 : 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
        {preview.products.map((item, i) => (
          <div key={`${item.productId}-${i}`}>
            <div style={summaryRow}>
              <span
                style={{ color: '#333', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={item.name}
              >
                {item.name}
              </span>
              <span style={{ color: '#333', whiteSpace: 'nowrap', marginLeft: 8 }}>{money(item.price * item.quantity)}</span>
            </div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>
              {item.quantity} × {money(item.price)}
            </div>
            {upsell?.productId === item.productId && (
              <div style={{ fontSize: 11, color: '#1a7f37', marginTop: 1 }}>
                Akcijska ponuda (umesto {money(upsell.regularPrice)}, ušteda {money(upsell.discountAmount)})
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ borderTop: '1px solid #e8e8e8', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={summaryRow}>
          <span style={{ color: '#555' }}>Međuzbir</span>
          <span>{money(preview.subtotal)}</span>
        </div>
        {vipSaving > 0.001 && (
          // Line prices are already VIP prices — informational, not another deduction.
          <div style={{ fontSize: 11, color: '#9a6a00', marginTop: -4 }}>
            VIP cene primenjene (ušteda {money(vipSaving)})
          </div>
        )}
        {quantityDiscount > 0 && (
          <div style={discountRow}>
            <span>Popust na količinu</span>
            <span>−{money(quantityDiscount)}</span>
          </div>
        )}
        {referralDiscount > 0 && (
          <div style={discountRow}>
            <span>Popust preporuke{preview.referral?.totalPercent ? ` (${preview.referral.totalPercent}%)` : ''}</span>
            <span>−{money(referralDiscount)}</span>
          </div>
        )}
        <div style={summaryRow}>
          <span style={{ color: '#555' }}>Dostava (pouzećem)</span>
          {preview.deliveryFee === 'free' ? (
            <span style={{ color: '#1a7f37', fontWeight: 600 }}>
              {preview.deliveryFeeMeta?.pendingFreeDeliveryUsed ? 'Besplatna (kredit)' : 'Besplatna'}
            </span>
          ) : (
            <span>{money(preview.deliveryFee)}</span>
          )}
        </div>
        <div style={{
          ...summaryRow,
          fontSize: 14,
          fontWeight: 700,
          borderTop: '1px solid #e8e8e8',
          paddingTop: 8,
          marginTop: 2,
        }}>
          <span>Ukupno</span>
          <span>{money(preview.total)}</span>
        </div>
      </div>

      {preview.hasError && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message={preview.errorMessages.map((m) => <div key={m}>{m}</div>)}
        />
      )}
    </div>
  );
};

BackendSummary.propTypes = {
  preview: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

CreateOrderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(['create', 'edit']),
  order: PropTypes.object,
};

export default CreateOrderModal;
